import { useRef } from 'react'

import { TransferResolverContext } from '~/components/forms/transfer/transferSchema'
import { getBridgeTokenType } from '~/constants/addresses'
import { useActiveOrg } from '~/hooks/org'
import { useOrgContacts } from '~/hooks/useOrgContacts'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { Address } from '~/utils/address'

// The same object on each render. Each render writes the org's current bank
// accounts, contact send rules, and smart accounts into it, so every resolver
// run reads fresh values.
export const useTransferResolverContext = (): TransferResolverContext => {
  const { org } = useActiveOrg()
  const { contacts } = useOrgContacts()
  const { smartAccounts, isLoading: isLoadingSmartAccounts } =
    useSmartAccounts()
  const resolverContext = useRef<TransferResolverContext>({
    bankAccounts: [],
    contactSendRules: {},
    smartAccountAddresses: undefined,
  }).current

  resolverContext.bankAccounts = (org?.bridge?.externalAccounts ?? []).map(
    (account) => ({
      address: account.liquidationAddress,
      isEurc:
        getBridgeTokenType(
          account.liquidationChainId,
          Address.format(account.liquidationToken),
        ) === 'eurc',
    }),
  )

  resolverContext.contactSendRules = Object.fromEntries(
    contacts
      .filter((contact) => contact.sendRules.length > 0)
      .map((contact) => [contact.address.toLowerCase(), contact.sendRules]),
  )

  resolverContext.smartAccountAddresses = isLoadingSmartAccounts
    ? undefined
    : smartAccounts.map((account) => account.address.toLowerCase())

  return resolverContext
}
